import { useMemo } from "react";
import type { DeepReadonly } from "ts-essentials";

import { readState } from "@/__main__/app-state.mjs";
import type { UserSubscription } from "@/feature-wallet/models/user-subscriptions.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

type SubscriptionWithStatus = DeepReadonly<
  Pick<UserSubscription, "status" | "trialEnd">
>;

export function isTrialSubscription<T extends SubscriptionWithStatus>(
  subscription: T,
): subscription is T & { status: "trialing"; trialEnd: Date } {
  return (
    subscription.status === "trialing" && subscription.trialEnd > new Date()
  );
}

export function isSubscriptionActive(subscription: SubscriptionWithStatus) {
  return subscription.status === "active" || isTrialSubscription(subscription);
}

// We use a generic here to return the same type as the input array.
export function getActiveSubscriptions<S extends SubscriptionWithStatus>({
  subscriptions,
}: {
  subscriptions: ReadonlyArray<S>;
}): ReadonlyArray<S> {
  return subscriptions.filter(isSubscriptionActive);
}

export function useActiveSubscriptions() {
  const { subscriptions } = useSnapshot(readState.wallet);

  return useMemo(
    () => getActiveSubscriptions({ subscriptions }),
    [subscriptions],
  );
}

const SUBSCRIPTION_ERROR_STATUS = [
  "past_due",
  "unpaid",
  "incomplete",
  "incomplete_expired",
];

export function hasSubscriptionError(subscription: SubscriptionWithStatus) {
  return SUBSCRIPTION_ERROR_STATUS.includes(subscription.status);
}

// We use a generic here to return the same type as the input array.
export function getSubscriptionsWithErrors<S extends SubscriptionWithStatus>({
  subscriptions,
}: {
  subscriptions: ReadonlyArray<S>;
}): ReadonlyArray<S> {
  return subscriptions.filter(hasSubscriptionError);
}

export function useSubscriptionsWithErrors() {
  const { subscriptions } = useSnapshot(readState.wallet);

  return useMemo(
    () => getSubscriptionsWithErrors({ subscriptions }),
    [subscriptions],
  );
}

export function isSubscriptionOngoing(subscription: SubscriptionWithStatus) {
  return (
    isSubscriptionActive(subscription) || hasSubscriptionError(subscription)
  );
}

// We use a generic here to return the same type as the input array.
export function getOngoingSubscriptions<S extends SubscriptionWithStatus>(
  subscriptions: ReadonlyArray<S>,
): ReadonlyArray<S> {
  return subscriptions.filter(isSubscriptionOngoing);
}

export function useOngoingSubscriptions() {
  const { subscriptions } = useSnapshot(readState.wallet);

  return useMemo(() => getOngoingSubscriptions(subscriptions), [subscriptions]);
}
